import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Edit, Trash2, BookOpen } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { useDeleteSubject } from '../../api/subject/subjectQueries';

export default function SubjectActions({ subject, onEdit, onDelete }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const { mutate: deleteSubject } = useDeleteSubject();
  
  // Update dropdown position based on button position
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      
      // Calculate space available below and to the right
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceRight = window.innerWidth - rect.right;
      
      // Position dropdown
      let top, left;
      
      // Determine vertical position (default: below button)
      if (spaceBelow < 150) {
        // Not enough space below, place above
        top = rect.top + scrollY - 8 - (dropdownRef.current ? dropdownRef.current.offsetHeight : 0);
      } else {
        // Enough space below
        top = rect.bottom + scrollY + 8;
      }
      
      // Determine horizontal position (default: aligned to right edge of button)
      if (spaceRight < 192) { // 48px (menu width) * 4 = 192
        // Not enough space to the right, align to left edge
        left = rect.left + scrollX - (dropdownRef.current ? dropdownRef.current.offsetWidth : 0) + rect.width;
      } else {
        // Align to right edge of button
        left = rect.right + scrollX - (dropdownRef.current ? dropdownRef.current.offsetWidth : 0);
      }
      
      setPosition({ top, left });
    }
  }, [showDropdown]);
  
  // Handle click outside
  useEffect(() => {
    if (!showDropdown) return;
    
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);
  
  const handleEdit = () => {
    onEdit(subject);
    setShowDropdown(false);
  };
  
  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${subject.name}?`)) {
      if (onDelete) {
        onDelete(subject.id);
      } else {
        deleteSubject(subject.id);
      }
    }
    setShowDropdown(false);
  };
  
  const handleViewDetails = () => {
    console.log(`View details for ${subject.name}`);
    setShowDropdown(false);
  };
  
  const toggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };
  
  // Portal component to render dropdown in document body
  const DropdownPortal = () => {
    if (!showDropdown) return null;
    
    return createPortal(
      <div 
        ref={dropdownRef}
        className="fixed w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]"
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
      >
        <div className="py-1" role="menu" aria-orientation="vertical">
          <button
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
            onClick={handleViewDetails}
            role="menuitem"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            View Details
          </button>
          <button
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
            onClick={handleEdit}
            role="menuitem"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </button>
          <button
            className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 w-full text-left"
            onClick={handleDelete}
            role="menuitem"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </button>
        </div>
      </div>,
      document.body
    );
  };
  
  return (
    <>
      <div className="relative">
        <Button 
          ref={buttonRef}
          variant="ghost" 
          size="icon" 
          className="h-8 w-8 rounded-full"
          onClick={toggleDropdown}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </div>
      <DropdownPortal />
    </>
  );
}